import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Tooltip, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import ConfirmModal from '@/components/modals/utils/ConfirmModal';
import {
  disablePrototyper,
  formatPrototypeFirmwareError,
} from '@/repository/PrototypeFirmwareDAO';
import {
  useToast,
  ToastType,
} from '@/context_providers/toast_context/ToastContext';
import type { PrototyperUserInfo } from '@/models/PrototypeFirmware/PrototyperUserInfo';
import COLORS from '@/resources/style/Themes';

type Props = {
  prototypers: PrototyperUserInfo[];
  refetchPrototypers: () => Promise<void>;
  refetchFirmwares: () => Promise<void>;
};

const ActiveProtoTypersTab: React.FC<Props> = ({
  prototypers,
  refetchPrototypers,
  refetchFirmwares,
}) => {
  const { showToast } = useToast();
  const [target, setTarget] = useState<PrototyperUserInfo | null>(null);
  const [busy, setBusy] = useState(false);

  const columns = useMemo<GridColDef<PrototyperUserInfo>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        headerClassName: 'first',
        cellClassName: 'first',
        valueGetter: (_v, row) => `${row.first_name} ${row.last_name}`,
      },
      {
        field: 'auth_id',
        headerName: 'Auth ID',
        flex: 1,
        minWidth: 200,
        renderCell: (p) => (
          <Typography
            component="span"
            sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}
          >
            {p.value as string}
          </Typography>
        ),
      },
      {
        field: 'phone_number',
        headerName: 'Phone',
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: 'org_code',
        headerName: 'Organization',
        flex: 0.8,
        minWidth: 140,
      },
      {
        field: 'assigned_firmwares',
        headerName: 'Assigned Firmwares',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (_v, row) =>
          (row.assigned_firmwares ?? [])
            .map((f) => `${f.name} v${f.version}`)
            .join(', '),
        renderCell: (p) => (
          <Tooltip title={p.value as string} placement="top-start">
            <Typography
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {p.value as string}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        flex: 0.9,
        minWidth: 180,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            variant="contained"
            size="small"
            sx={{
              backgroundColor: COLORS.PINK,
              color: COLORS.DARK_RED,
              '&:hover': { backgroundColor: COLORS.HOVER_RED },
            }}
            onClick={() => setTarget(params.row)}
          >
            Remove Prototyper
          </Button>
        ),
      },
    ],
    [],
  );

  const handleConfirm = async () => {
    if (!target || busy) return;
    setBusy(true);
    try {
      await disablePrototyper(target.auth_id);
    } catch (error) {
      const { message, shouldRefetch } = formatPrototypeFirmwareError(
        error,
        'Prototyper',
      );
      showToast(message, ToastType.DANGER);
      if (shouldRefetch) {
        try {
          await Promise.all([refetchPrototypers(), refetchFirmwares()]);
        } catch {
          // parent's refetch toast covers this
        }
      }
      setBusy(false);
      return;
    }
    showToast(
      `Removed prototyper access from ${target.first_name} ${target.last_name}.`,
      ToastType.SUCCESS,
    );
    try {
      await Promise.all([refetchPrototypers(), refetchFirmwares()]);
    } catch {
      // refetch failures are surfaced by the parent's own toast.
    }
    setTarget(null);
    setBusy(false);
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        To grant prototyper access, use the &quot;Make Prototyper&quot; button
        on a user in Group Management.
      </Alert>
      <NavigatorDataGrid<PrototyperUserInfo>
        rows={prototypers}
        columns={columns}
        getRowId={(row) => row.auth_id}
        initialPageSize={15}
      />
      <ConfirmModal
        show={!!target}
        confirm={handleConfirm}
        onHide={() => {
          if (!busy) setTarget(null);
        }}
        confirmTitle={
          target
            ? `Remove Prototyper Access from ${target.first_name} ${target.last_name}`
            : ''
        }
        confirmMessage={
          target
            ? `Are you sure you want to revoke ${target.first_name} ${target.last_name}'s prototype firmware access? Any Compass device currently running prototype firmware will be prompted to revert to production firmware the next time it connects to their app.`
            : ''
        }
      />
    </Box>
  );
};

export default ActiveProtoTypersTab;
